import { useEffect, useMemo, useState } from 'react'
import { navigate, PageProps } from 'gatsby'
import moment from 'moment'
import clsx from 'clsx'
import { useAsyncEffect, useBoolean } from 'ahooks'
import { decode as decodeBase64 } from 'base-64'
import Layout from 'components/layouts/tow-column'

import {
  LedgerItemStatus,
  useLedgerItemArchiveMutation,
  useLedgerItemQuery,
  useLedgerItemUnarchiveMutation,
  useLedgerItemUpdateMutation,
} from 'lib/graphql/storeadmin'
import {
  Button,
  ErrorAlert,
  Form,
  SEO,
  showMessage,
  Space,
} from 'components/common'
import {
  LedgerItemStatusCard,
  LedgerSingleItemCard,
  SelectAndDescription,
  SelectAndDescriptionFormValues,
} from 'components/ledgers'
import { ellipsisText, ledgerTypeCheck } from 'utils/custom-functions'
import { date as strDate } from 'lib/str-formatter'
import { ledgerItemErrorHandler } from 'lib/hooks/ledger'

const onBack = () => navigate('../')

interface ledgerItemUpdateType extends SelectAndDescriptionFormValues {
  status: LedgerItemStatus
}

export const Head = () => <SEO title="項目詳細" />

const IdPage = ({ location, params: { id } }: PageProps): JSX.Element => {
  const idDecoded = decodeBase64(id).split(':')[1]
  const { data, loading, error, refetch } = useLedgerItemQuery({
    variables: {
      id: idDecoded.toString(),
      ledgerQuery: {
        month: moment().format('YYYY-MM'),
      },
    },
  })
  const [date, setDate] = useState<moment.Moment>(moment())

  // Use ledger Item Archive/UnArchive Mutation
  const [archived] = useLedgerItemArchiveMutation()
  const [unarchived] = useLedgerItemUnarchiveMutation()

  // Ledger Item Update Mutation
  const [update] = useLedgerItemUpdateMutation()

  //  ledgerSingleItem error useState
  const [showLedgerErrorAlert, setShowLedgerErrorAlert] = useBoolean(false)
  const [ledgerErrorDescriptions, setLedgerErrorDescriptions] = useState<
    string[]
  >([])
  const [requesting, setRequesting] = useBoolean(false)
  const [form] = Form.useForm<SelectAndDescriptionFormValues>()

  const dataSource = useMemo(() => {
    const ledgerSingleItemData = []
    data?.ledgerItem?.ledger?.transactions.forEach((transaction) => {
      const { id, amount, date, type, total, number } = transaction

      ledgerSingleItemData.push({
        key: id,
        amount: amount,
        balance: total,
        date: strDate(moment(date)),
        number: `${number}`,
        type: ledgerTypeCheck(type),
      })
    })
    return ledgerSingleItemData
  }, [data])

  const selectAndDescriptionMemo = useMemo(() => {
    const title = data?.ledgerItem?.title
    const ellipsisTitle = ellipsisText(title, 10)
    const ledgerItemCategory = data?.ledgerItem?.ledgerItemCategory
    return {
      title: title,
      ledgerItemCategory: ledgerItemCategory,
      description: data?.ledgerItem?.description,
      ellipsisTitle: ellipsisTitle?.text,
    }
  }, [data])

  useAsyncEffect(async () => {
    await refetch()
  }, [location, refetch])

  useEffect(() => {
    form.setFieldsValue(selectAndDescriptionMemo)
  }, [selectAndDescriptionMemo, form])

  const onSubmit = async (values: ledgerItemUpdateType) => {
    const title = form.getFieldValue('title')
    try {
      const updateInput: ledgerItemUpdateType = {
        ...values,
        title: title,
      }

      const { data } = await update({
        variables: {
          id: idDecoded,
          inputs: updateInput,
        },
      })
      const { ledgerItemUserErrors } = data.ledgerItemUpdate
      if (ledgerItemUserErrors.length > 0) {
        const errorDescriptions = ledgerItemErrorHandler(ledgerItemUserErrors)
        setShowLedgerErrorAlert.setTrue()
        setLedgerErrorDescriptions(errorDescriptions)
        showMessage(
          'error',
          <>
            <div>項目の更新中にエラーが発生しました。</div>
            <div>{errorDescriptions.join('\n')}</div>
          </>
        )
      } else if (error) {
        throw error
      } else {
        setShowLedgerErrorAlert.setFalse()
        showMessage('success', '項目を更新しました。')
      }
    } catch (e) {
      const duplicateTitleError =
        e.message.search('Tried to save duplicate unique keys') > -1 &&
        '項目名が重複しています'
      showMessage('error', '項目の更新処理時に予期せぬエラーが発生しました。')
      if (duplicateTitleError) {
        setShowLedgerErrorAlert.setTrue()
        setLedgerErrorDescriptions([duplicateTitleError])
      }
      console.error(e)
    } finally {
      setRequesting.setFalse()
      refetch()
    }
  }

  const onChangeDate = (date: moment.Moment) => {
    setDate(date)
    refetch({
      id: idDecoded.toString(),
      ledgerQuery: {
        month: date?.format('YYYY-MM'),
      },
    })
  }

  const archive = async (): Promise<void> => {
    try {
      await archived({
        variables: {
          id: idDecoded.toString(),
        },
      })
      showMessage('success', 'アーカイブしました。')
    } catch (e) {
      showMessage('error', 'アーカイブ処理時に予期せぬエラーが発生しました。')
      console.error(e)
    } finally {
      refetch()
    }
  }

  const unarchive = async (): Promise<void> => {
    try {
      await unarchived({
        variables: {
          id: idDecoded.toString(),
        },
      })
      showMessage('success', 'アクティブになりました。')
    } catch (e) {
      showMessage('error', 'アクティブ化時に予期せぬエラーが発生しました。')
      console.error(e)
    } finally {
      refetch()
    }
  }
  return (
    <>
      <Form
        form={form}
        component="form"
        onFinish={onSubmit}
        name="LedgerItemsEditForm"
      >
        <Layout title={selectAndDescriptionMemo?.ellipsisTitle} onBack={onBack}>
          <Layout.Section size="large">
            <Layout.Item>
              {showLedgerErrorAlert && (
                <ErrorAlert
                  onCloseAlert={setShowLedgerErrorAlert.setFalse}
                  alertDescription={ledgerErrorDescriptions}
                />
              )}
              <SelectAndDescription
                title={selectAndDescriptionMemo?.title}
                description={selectAndDescriptionMemo?.description || ''}
                maxDescriptionLength={256}
              />
            </Layout.Item>
            <Layout.Item>
              <LedgerSingleItemCard
                date={date}
                dataSource={dataSource}
                urlState={{
                  id: idDecoded,
                  month: date?.format('YYYY-MM'),
                  title: selectAndDescriptionMemo?.title,
                }}
                loading={loading}
                handleViewDay={onChangeDate}
              />
            </Layout.Item>
          </Layout.Section>
          <Layout.Section size="small">
            <LedgerItemStatusCard
              onUnarchive={unarchive}
              status={data?.ledgerItem?.status || LedgerItemStatus.Active}
            />
          </Layout.Section>
          <Layout.Footer
            left={
              <Space>
                {data?.ledgerItem?.status === LedgerItemStatus.Archived ? (
                  <Space>
                    <Button disabled={loading} onClick={unarchive}>
                      アーカイブを解除する
                    </Button>
                  </Space>
                ) : (
                  <Space>
                    <Button disabled={loading} onClick={archive}>
                      アーカイブする
                    </Button>
                  </Space>
                )}
              </Space>
            }
            right={
              <div className={clsx('flex', 'justify-end')}>
                <Form.Item noStyle>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={requesting}
                    disabled={loading}
                  >
                    保存する
                  </Button>
                </Form.Item>
              </div>
            }
          />
        </Layout>
      </Form>
    </>
  )
}
export default IdPage
